import {
  Bell,
  MessageSquare,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Calendar,
  Megaphone,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/lib/utils';
import { PORTAL_CONFIG, type PortalRole } from '@/lib/constants';
import type { Notification } from '@socialsoup/shared';
import { NotificationType } from '@socialsoup/shared';

interface NotificationItemProps {
  notification: Notification;
  onMarkRead: (id: string) => void;
  portal: PortalRole;
  /** Admin-only: show an email delivery indicator (dot + tooltip) when set. */
  showDeliveryStatus?: boolean;
}

/** Email delivery indicator config, or null when no email was attempted. */
function deliveryIndicator(status: Notification['emailStatus']) {
  switch (status) {
    case 'SENT':
      return { dot: 'bg-status-approved-text', label: 'Email delivered' };
    case 'PENDING':
      return { dot: 'bg-status-pending-text', label: 'Email pending' };
    case 'FAILED':
      return { dot: 'bg-status-rejected', label: 'Email failed' };
    default:
      return null;
  }
}

function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case NotificationType.SUBMISSION_STATUS:
      return {
        icon: CheckCircle2,
        bg: 'bg-status-approved-bg',
        color: 'text-status-approved-text',
      };
    case NotificationType.FEEDBACK:
      return { icon: MessageSquare, bg: 'bg-status-review-bg', color: 'text-status-review-text' };
    case NotificationType.CAMPAIGN_ASSIGNED:
      return {
        icon: Megaphone,
        bg: 'bg-portal-influencer/10',
        color: 'text-portal-influencer-foreground',
      };
    case NotificationType.CAMPAIGN_UPDATED:
      return {
        icon: Megaphone,
        bg: 'bg-portal-agent/20',
        color: 'text-portal-agent-foreground',
      };
    case NotificationType.DEADLINE_REMINDER:
      return { icon: Calendar, bg: 'bg-status-pending-bg', color: 'text-status-pending-text' };
    case NotificationType.SYSTEM:
      return { icon: AlertCircle, bg: 'bg-status-draft-bg', color: 'text-status-draft-text' };
    case NotificationType.PIPELINE_FAILED:
      return { icon: AlertTriangle, bg: 'bg-status-rejected-bg', color: 'text-status-rejected' };
    default:
      return { icon: Bell, bg: 'bg-status-draft-bg', color: 'text-status-draft-text' };
  }
}

export function NotificationItem({
  notification,
  onMarkRead,
  portal,
  showDeliveryStatus = false,
}: NotificationItemProps) {
  const { icon: Icon, bg, color } = getNotificationIcon(notification.type as NotificationType);
  const delivery = showDeliveryStatus ? deliveryIndicator(notification.emailStatus) : null;

  const handleClick = () => {
    if (!notification.isRead) {
      onMarkRead(notification.id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      className={cn(
        'flex gap-3 p-4 cursor-pointer transition-colors',
        'hover:bg-muted',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset',
        !notification.isRead && 'bg-status-pending-bg',
      )}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={`${notification.isRead ? '' : 'Unread: '}${notification.title}`}
    >
      <div className={cn('w-10 h-10 rounded-full flex items-center justify-center shrink-0', bg)}>
        <Icon className={cn('h-5 w-5', color)} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p
            className={cn(
              'text-sm',
              notification.isRead ? 'text-content-secondary' : 'text-content-primary font-medium',
            )}
          >
            {notification.title}
          </p>
          {!notification.isRead && (
            <span
              className="w-2 h-2 rounded-full shrink-0 mt-1.5"
              style={{ backgroundColor: PORTAL_CONFIG[portal].accentCssValue }}
              aria-hidden="true"
            />
          )}
        </div>
        <p className="text-sm text-content-muted mt-0.5 line-clamp-2">{notification.message}</p>
        <div className="flex items-center gap-2 mt-1">
          <p className="text-xs text-content-muted">{formatRelativeTime(notification.createdAt)}</p>
          {delivery && (
            <span
              className="inline-flex items-center gap-1 text-xs text-content-muted"
              title={
                notification.emailStatus === 'FAILED' && notification.emailError
                  ? `${delivery.label}: ${notification.emailError}`
                  : delivery.label
              }
            >
              <span className={cn('w-1.5 h-1.5 rounded-full', delivery.dot)} aria-hidden="true" />
              {delivery.label}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
